import { UseQueryOptions } from "@tanstack/react-query"

import { useMutationHook, useQueryHook } from "@/hooks"
import {
  createNewPlayer,
  joinPlayer,
  readAssignablePlayers,
  readParticipateActivity,
  readPlayerInfo,
  upsertMovePlayers,
  upsertPlayer,
  upsertPlayerGroup,
  upsertPlayerLeave,
  upsertPlayerWithDevice,
} from "@/app/api/player"
import {
  Player,
  ReadParticipateActivityParams,
  ReadPlayerInfoParams,
  ReadPlayerInfoResponse,
  TeamPlayer,
} from "@/types/player"
import { ApiError } from "@/app/api/type"

export const useAssignablePlayersQuery = () => {
  return useQueryHook<Player[], unknown>({
    queryKey: ["readAssignablePlayers"],
    queryFn: readAssignablePlayers,
  })
}

export const usePlayerInfoQuery = (params: ReadPlayerInfoParams) => {
  return useQueryHook<ReadPlayerInfoResponse, ReadPlayerInfoParams>({
    queryKey: ["readPlayerInfo", ...Object.values(params)],
    queryFn: async () => await readPlayerInfo(params),
    options: { enabled: !!params.idfPlayer } as UseQueryOptions<TeamPlayer, ApiError>,
  })
}

export const useParticipatedPlayersQuery = (params: ReadParticipateActivityParams) => {
  return useQueryHook({
    queryKey: ["readParticipatedPlayers", ...Object.values(params)],
    queryFn: async () => await readParticipateActivity(params),
  })
}

export const useCreatePlayerMutation = () => {
  return useMutationHook(createNewPlayer, [
    ["readAccountInfo"],
    ["readPlayers"],
    ["readPlayerInfo"],
    ["readAssignablePlayers"],
  ])
}

export const useJoinPlayerMutation = () => {
  return useMutationHook(joinPlayer, [
    ["readAccountInfo"],
    ["readPlayers"],
    ["readPlayerInfo"],
    ["readAssignablePlayers"],
  ])
}

export const useUpsertPlayerMutation = (params: ReadPlayerInfoParams) => {
  return useMutationHook(upsertPlayer, [
    ["readAccountInfo"],
    ["readPlayerInfo", ...Object.values(params)],
    ["readPlayers"],
    ["readGroupPlayers"],
  ])
}

export const useUpsertPlayerWithDeviceMutation = (params: ReadPlayerInfoParams) => {
  return useMutationHook(upsertPlayerWithDevice, [
    ["readAccountInfo"],
    ["readPlayerInfo", ...Object.values(params)],
    ["readPlayers"],
    ["readGroupPlayers"],
    ["readMatchedDevices"],
    ["readAssignablePlayers"],
    ["readAssignableDevices"],
  ])
}

export const useUpsertPlayerLeaveMutation = () => {
  return useMutationHook(upsertPlayerLeave, [
    ["readAccountInfo"],
    ["readPlayers"],
    ["readPlayerGroups"],
    ["readGroupPlayers"],
    ["readPlayerInfo"],
    ["readMatchedDevices"],
    ["readAssignablePlayers"],
    ["readAssignableDevices"],
  ])
}

export const useUpsertPlayerGroupMutation = () => {
  return useMutationHook(upsertPlayerGroup, [
    ["readPlayerInfo"],
    ["readPlayers"],
    ["readPlayerGroups"],
    ["readGroupPlayers"],
  ])
}

export const useUpsertMovePlayersMutation = () => {
  return useMutationHook(upsertMovePlayers, [
    ["readAccountInfo"],
    ["readPlayers"],
    ["readPlayerGroups"],
    ["readGroupPlayers"],
    ["readPlayerInfo"],
    ["readAssignablePlayers"],
  ])
}
